import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { User, Building2 } from 'lucide-react';
import Linkify from '@/lib/linkify';
import MemberAvatar from '@/components/MemberAvatar';

const BACKEND_URL = '';

const MENTION_RE = /([@#])\[([^\]]+)\]\(([^)]+)\)/g;

/** Convert storage format → display format, and extract mentions into a Map */
function parseStored(stored) {
  const map = new Map();
  const display = stored.replace(MENTION_RE, (_, sym, label, id) => {
    const key = `${sym}${label}`;
    map.set(key, { symbol: sym, label, id, type: sym === '@' ? 'user' : 'company' });
    return key;
  });
  return { display, map };
}

/** Convert display format → storage format using the mentions map */
function toStored(display, map) {
  let result = display;
  // Sort by longest key first to avoid partial replacements
  const entries = [...map.entries()].sort((a, b) => b[0].length - a[0].length);
  for (const [key, info] of entries) {
    const stored = `${info.symbol}[${info.label}](${info.id})`;
    result = result.split(key).join(stored);
  }
  return result;
}

/**
 * MentionInput – a textarea with @user and #company autocomplete.
 * Props:
 *   value, onChange, onSubmit, placeholder, className,
 *   disabled, testId, rows (default 1), singleLine (default false)
 */
export function MentionInput({
  value, onChange, onSubmit, placeholder, className = '',
  disabled, testId, rows = 1, singleLine = false,
}) {
  const mentionsMap = useRef(new Map());
  const [displayValue, setDisplayValue] = useState('');
  const lastParentValue = useRef(value);
  const [mentionState, setMentionState] = useState({ active: false, type: null, query: '', startIdx: -1 });
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextParentSync = useRef(false);

  // Sync when parent value changes externally (e.g., loading edit form, clearing)
  useEffect(() => {
    if (skipNextParentSync.current) {
      skipNextParentSync.current = false;
      lastParentValue.current = value;
      return;
    }
    if (value !== lastParentValue.current) {
      const { display, map } = parseStored(value || '');
      mentionsMap.current = map;
      setDisplayValue(display);
      lastParentValue.current = value;
    }
  }, [value]);

  // Initialize on first render
  useEffect(() => {
    const { display, map } = parseStored(value || '');
    mentionsMap.current = map;
    setDisplayValue(display);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect @ or # trigger in display text
  const detectMention = useCallback((text, cursorPos) => {
    const before = text.slice(0, cursorPos);
    const atMatch = before.match(/(?:^|[\s(])@([\w\s]*)$/);
    const hashMatch = before.match(/(?:^|[\s(])#([\w\s]*)$/);
    if (atMatch) {
      const q = atMatch[1];
      const startIdx = before.length - atMatch[0].length + (atMatch[0].startsWith('@') ? 0 : 1);
      return { active: true, type: 'user', query: q, startIdx };
    }
    if (hashMatch) {
      const q = hashMatch[1];
      const startIdx = before.length - hashMatch[0].length + (hashMatch[0].startsWith('#') ? 0 : 1);
      return { active: true, type: 'company', query: q, startIdx };
    }
    return { active: false, type: null, query: '', startIdx: -1 };
  }, []);

  // Fetch suggestions
  useEffect(() => {
    if (!mentionState.active || !mentionState.query) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const endpoint = mentionState.type === 'user'
          ? `${BACKEND_URL}/api/members/search?q=${encodeURIComponent(mentionState.query)}`
          : `${BACKEND_URL}/api/companies/search?q=${encodeURIComponent(mentionState.query)}`;
        const res = await fetch(endpoint, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.slice(0, 6));
          setSelectedIdx(0);
        }
      } catch {}
      setLoading(false);
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [mentionState.active, mentionState.query, mentionState.type]);

  // Prune deleted mentions from map
  const pruneMap = useCallback((text) => {
    for (const key of mentionsMap.current.keys()) {
      if (!text.includes(key)) {
        mentionsMap.current.delete(key);
      }
    }
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    setDisplayValue(text);
    pruneMap(text);
    const stored = toStored(text, mentionsMap.current);
    lastParentValue.current = stored;
    skipNextParentSync.current = true;
    onChange(stored);
    const cursor = e.target.selectionStart;
    setMentionState(detectMention(text, cursor));
  };

  const insertMention = (item) => {
    const symbol = mentionState.type === 'user' ? '@' : '#';
    const label = item.name;
    const id = mentionState.type === 'user' ? item.user_id : item.company_id;
    const displayTag = `${symbol}${label}`;

    // Add to map
    mentionsMap.current.set(displayTag, { symbol, label, id, type: mentionState.type });

    // Insert clean display text
    const before = displayValue.slice(0, mentionState.startIdx);
    const cursorPos = inputRef.current?.selectionStart || displayValue.length;
    const after = displayValue.slice(cursorPos);
    const newDisplay = before + displayTag + ' ' + after.trimStart();
    setDisplayValue(newDisplay);

    // Convert to storage and notify parent
    const stored = toStored(newDisplay, mentionsMap.current);
    lastParentValue.current = stored;
    skipNextParentSync.current = true;
    onChange(stored);

    setMentionState({ active: false, type: null, query: '', startIdx: -1 });
    setSuggestions([]);

    setTimeout(() => {
      if (inputRef.current) {
        const pos = before.length + displayTag.length + 1;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (mentionState.active && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(i => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(i => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionState({ active: false, type: null, query: '', startIdx: -1 });
        setSuggestions([]);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && onSubmit && !mentionState.active) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setMentionState(s => ({ ...s, active: false }));
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Tag = singleLine ? 'input' : 'textarea';

  return (
    <div className="relative" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <Tag
        ref={inputRef}
        type={singleLine ? 'text' : undefined}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={singleLine ? undefined : rows}
        data-testid={testId}
        className={`w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm
          ring-offset-white placeholder:text-slate-400 focus:outline-none focus:ring-2
          focus:ring-brand-navy/20 focus:border-brand-navy/40 disabled:opacity-50
          ${singleLine ? 'h-9' : 'min-h-[36px] resize-none'} ${className}`}
      />

      {mentionState.active && suggestions.length > 0 && (
        <div ref={dropdownRef}
          className="absolute left-0 bottom-full mb-1 w-64 bg-white rounded-lg border border-slate-200
            shadow-lg z-50 max-h-48 overflow-y-auto py-1"
          data-testid="mention-suggestions">
          {suggestions.map((item, i) => (
            <button
              key={item.user_id || item.company_id}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors
                ${i === selectedIdx ? 'bg-brand-navy/5 text-brand-navy' : 'text-slate-700 hover:bg-slate-50'}`}
              onMouseDown={(e) => { e.preventDefault(); insertMention(item); }}
              onMouseEnter={() => setSelectedIdx(i)}
              data-testid={`mention-item-${item.user_id || item.company_id}`}
            >
              {mentionState.type === 'user' ? (
                <MemberAvatar name={item.name} picture={item.picture} size={24} />
              ) : (
                <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-3 w-3 text-slate-500" />
                </div>
              )}
              <span className="truncate font-medium">{item.name}</span>
              {item.sector && <span className="text-xs text-slate-400 truncate ml-auto">{item.sector}</span>}
            </button>
          ))}
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching...</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Renders text with @[Name](id) and #[Company](id) as styled links.
 * Plain text segments are also linkified so bare URLs become clickable.
 */
export function RenderMentions({ text, onUserClick, onCompanyClick }) {
  if (!text) return null;

  const parts = [];
  let lastIdx = 0;
  const regex = /([@#])\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<Linkify key={`pre-${lastIdx}`} text={text.slice(lastIdx, match.index)} />);
    }
    const symbol = match[1];
    const label = match[2];
    const id = match[3];
    if (symbol === '@') {
      parts.push(
        <button key={match.index} onClick={() => onUserClick?.(id)}
          className="inline text-brand-navy font-semibold hover:underline cursor-pointer"
          data-testid={`mention-user-${id}`}>
          {label}
        </button>
      );
    } else {
      parts.push(
        <button key={match.index} onClick={() => onCompanyClick?.(id)}
          className="inline text-brand-sage font-semibold hover:underline cursor-pointer"
          data-testid={`mention-company-${id}`}>
          #{label}
        </button>
      );
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(<Linkify key={`tail-${lastIdx}`} text={text.slice(lastIdx)} />);
  }
  return <>{parts}</>;
}
